import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Stack,
  Link,
  DialogActions,
  IconButton,
  Alert,
  List,
  ListItem,
  Slide,
} from "@mui/material";
import * as XLSX from "xlsx";
import CloseIcon from "@mui/icons-material/Close";
import ReusableTable from "../../../../../../components/ReusableTable/ReusableTable";
import { useMutation } from "@apollo/client";
import { UPSERT_EMPLOYEES_MUTATION } from "../../../../../../apis/organization-apis/people-apis/peopleApis";
import { LoadingButton } from "@mui/lab";
import ConfirmSnackbar from "../../../../../../components/Snackbar/ConfirmSnackbar";
import moment from "moment";

const CsvBulkUploadDialog = ({ open, onClose, refetch, close }) => {
  const [upsertEmployees, { loading }] = useMutation(UPSERT_EMPLOYEES_MUTATION);
  const [snackbarInfo, setSnackbarInfo] = useState(null);

  const TransitionComponent = (props) => {
    return <Slide {...props} direction="left" />;
  };
  const [tableData, setTableData] = useState([]);
  const [fileName, setFileName] = useState("");
  const [errors, setErrors] = useState([]);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const fileInputRef = useRef(null);

  const columns = [
    { label: "Employee Number", field: "employee_number" },
    { label: "First Name", field: "first_name" },
    { label: "Middle Name", field: "middle_name" },
    { label: "Last Name", field: "last_name" },
    { label: "Name", field: "name" },
    { label: "Employee Email", field: "employee_email" },
    { label: "Manager Email", field: "manager_email" },
    { label: "Effective Date", field: "effective_date" },
    { label: "Job Title", field: "job_title" },
    { label: "Employment Type", field: "employment_type" },
    { label: "Termination Date", field: "termination_date" },
    { label: "Employment Status", field: "employment_status" },
  ];

  const validateData = (data) => {
    const validationErrors = [];
    const emailRowMap = new Map();
    const employeeNumberRowMap = new Map();

    data.forEach((row, index) => {
      const rowErrors = [];

      // Required fields validation
      if (!row.first_name) rowErrors.push("Missing  first name");
      if (!row.last_name) rowErrors.push("Missing last name");
      if (!row.employee_email) rowErrors.push("Missing  employee email");
      if (!row.employment_type) rowErrors.push("Missing  employment type");
      if (!row.employee_number) rowErrors.push("Missing  employee eumber");

      // Check for duplicate Employee Email
      if (row.employee_email) {
        if (emailRowMap.has(row.employee_email)) {
          const firstRow = emailRowMap.get(row.employee_email);
          rowErrors.push(
            `Duplicate employee email, same email found at row ${firstRow}`
          );
        } else {
          emailRowMap.set(row.employee_email, index + 1);
        }
      }

      // Check for duplicate Employee Number
      if (row.employee_number) {
        if (employeeNumberRowMap.has(row.employee_number)) {
          const firstRow = employeeNumberRowMap.get(row.employee_number);
          rowErrors.push(
            `Duplicate employee number, same number found at row ${firstRow}`
          );
        } else {
          employeeNumberRowMap.set(row.employee_number, index + 1);
        }
      }

      if (rowErrors.length > 0) {
        validationErrors.push({ row: index + 1, errors: rowErrors });
      }
    });

    return validationErrors;
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const parsedData = XLSX.utils.sheet_to_json(sheet, {
          raw: true,
          defval: "",
        });

        // Transform the data to match the API requirements
        const transformedData = parsedData.map((row) => ({
          first_name: row["First Name"] || null,
          middle_name: row["Middle Name"] || null,
          last_name: row["Last Name"] || null,
          name: `${row["First Name"] || null} ${row["Middle Name"]} ${
            row["Last Name"] || null
          }`.trim(),
          employee_email: row["Employee Email"] || null,
          manager_email: row["Manager Email"] || null,
          // effective_date:
          //   String(moment(row["Employment Start Date(DD/MM/YYYY)"])).format("YYYY-MM-DD")) || null,
          effective_date:
            String(moment(row["Employment Start Date"]).format("YYYY-MM-DD")) ||
            null,
          job_title: row["Job Title"] || null,
          employment_type: row["Employment Type"] || null,
          employee_number: String(row["Employee Number"]) || null,
          termination_date: row["Termination Date"]
            ? String(moment(row["Termination Date"]).format("YYYY-MM-DD"))
            : null,
          employment_status: row["Employment Status"] || null,
        }));

        const validationErrors = validateData(transformedData);
        if (validationErrors.length > 0) {
          setErrors(validationErrors);
          setErrorDialogOpen(true);
        } else {
          setTableData(transformedData);
        }
      } catch (error) {
        console.error("Error parsing file:", error);
        alert(
          "There was an issue with the file. Please ensure it’s a valid .csv or .xlsx file."
        );
      }
    };
    reader.readAsArrayBuffer(file);
    setFileName(file.name);
    fileInputRef.current.value = "";
  };

  const handleRemoveFile = () => {
    setTableData([]);
    setFileName("");
    fileInputRef.current.value = "";
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const downloadTemplate = () => {
    const templateData = [
      {
        "Employee Number": "EMP1234567",
        "First Name": "John",
        "Last Name": "Doe",
        "Middle Name": "A",
        "Employee Email": "john@example.com",
        "Employment Type": "part-time",
        "Employment Start Date": "2024-02-22",
        "Job Title": "SWE",
        "Manager Email": "jennydowney@example.com",
        "Termination Date": "2024-02-22",
        "Employment Status": "Active/Inactive",
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
    XLSX.writeFile(workbook, "Bulk_Upload_Template.xlsx");
  };

  const handleSubmit = async () => {
    try {
      const response = await upsertEmployees({
        variables: { employees: tableData },
      });

      refetch();
      close();
      onClose();
      handleRemoveFile();
      setSnackbarInfo({
        message: response?.data?.upsertEmployees.message,
        vertical: "top",
        horizontal: "right",
        Transition: TransitionComponent,
      });
    } catch (err) {
      console.error("Error during API submission:", err);
      // alert("An error occurred during upload. Please try again.");
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle fontWeight="bold">
          CSV Bulk Upload{" "}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ overflow: "hidden" }} dividers>
          <Typography variant="body2" gutterBottom>
            Fill in the details of multiple users according to the format
            mentioned in the template and upload it as a .csv file. Download
            this{" "}
            <Link href="#" underline="always" onClick={downloadTemplate}>
              template
            </Link>
            .
          </Typography>

          <Stack direction="row" alignItems="center" spacing={2} mt={2} mb={2}>
            <Button variant="outlined" onClick={handleUploadClick}>
              Upload a CSV
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv, .xlsx"
              style={{ display: "none" }}
              onChange={handleFileUpload}
            />
            {fileName && (
              <Typography variant="body2" color="#031B89">
                {fileName}
                <IconButton onClick={handleRemoveFile}>
                  <CloseIcon sx={{ color: "#031B89" }} />
                </IconButton>
              </Typography>
            )}
          </Stack>

          <ReusableTable
            columns={columns}
            data={tableData}
            renderCell={(row, column) => row[column.field] || "-"}
            height={"335px"}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              onClose();
              handleRemoveFile();
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            loading={loading}
            onClick={() => {
              if (errors.length > 0) {
                setErrorDialogOpen(true);
              } else {
                handleSubmit();
              }
            }}
          >
            Submit
          </LoadingButton>
        </DialogActions>

        {/* Error Dialog */}
        <Dialog
          open={errorDialogOpen}
          onClose={() => {
            setErrorDialogOpen(false);
            setErrors([]);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Error in CSV Data</DialogTitle>
          <DialogContent dividers>
            <Alert severity="error">Please correct the following errors:</Alert>
            <List>
              {errors.map((error, index) =>
                error.errors.map((err, i) => (
                  <ListItem key={`${index}-${i}`}>
                    <Typography variant="body2">
                      Row {error.row} : {err}
                    </Typography>
                  </ListItem>
                ))
              )}
            </List>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setErrorDialogOpen(false);
                setErrors([]);
              }}
              variant="contained"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Dialog>
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </>
  );
};

export default CsvBulkUploadDialog;
